'use client';

import { useState, useMemo, useCallback } from 'react';
import { Copy, Check, Terminal, Bell, ChevronDown, ChevronRight } from 'lucide-react';
import Alert24CTA from '@/components/Alert24CTA';

// ---------------------------------------------------------------------------
// Types & option tables
// ---------------------------------------------------------------------------

type Priority = '' | 'emerg' | 'alert' | 'crit' | 'err' | 'warning' | 'notice' | 'info' | 'debug';
type OutputFormat = '' | 'short' | 'short-iso' | 'cat' | 'json' | 'json-pretty' | 'verbose';

interface BuilderState {
  unit: string;
  priority: Priority;
  since: string;
  until: string;
  follow: boolean;
  kernel: boolean;
  boot: string; // '' = none, '0' = current, '-1' = previous, etc.
  grep: string;
  output: OutputFormat;
  reverse: boolean;
  lines: string;
  diskUsage: boolean;
  uid: string;
}

const DEFAULT_STATE: BuilderState = {
  unit: '',
  priority: '',
  since: '',
  until: '',
  follow: false,
  kernel: false,
  boot: '',
  grep: '',
  output: '',
  reverse: false,
  lines: '',
  diskUsage: false,
  uid: '',
};

// Numeric syslog priority used by the Alert24 journald log search (min_priority).
const PRIORITY_NUM: Record<Exclude<Priority, ''>, number> = {
  emerg: 0,
  alert: 1,
  crit: 2,
  err: 3,
  warning: 4,
  notice: 5,
  info: 6,
  debug: 7,
};

const UNIT_SUGGESTIONS = [
  'sshd',
  'nginx',
  'docker',
  'mysql',
  'postgresql',
  'cron',
  'systemd-networkd',
  'NetworkManager',
  'apache2',
  'redis',
  'kubelet',
];

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: '', label: 'Any priority' },
  { value: 'emerg', label: 'emerg (0) — system unusable' },
  { value: 'alert', label: 'alert (1) — action required now' },
  { value: 'crit', label: 'crit (2) — critical' },
  { value: 'err', label: 'err (3) — errors' },
  { value: 'warning', label: 'warning (4) — warnings' },
  { value: 'notice', label: 'notice (5) — normal but notable' },
  { value: 'info', label: 'info (6) — informational' },
  { value: 'debug', label: 'debug (7) — debug' },
];

const SINCE_PRESETS = ['', 'today', 'yesterday', '1 hour ago', '6 hours ago', '24 hours ago', '7 days ago'];
const UNTIL_PRESETS = ['', 'now', 'today', 'yesterday', '1 hour ago'];

const OUTPUT_OPTIONS: { value: OutputFormat; label: string }[] = [
  { value: '', label: 'Default (short)' },
  { value: 'short', label: 'short — classic syslog' },
  { value: 'short-iso', label: 'short-iso — ISO timestamps' },
  { value: 'cat', label: 'cat — message only' },
  { value: 'json', label: 'json — one object per line' },
  { value: 'json-pretty', label: 'json-pretty — pretty JSON' },
  { value: 'verbose', label: 'verbose — all fields' },
];

const BOOT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All boots' },
  { value: '0', label: 'Current boot (-b)' },
  { value: '-1', label: 'Previous boot (-b -1)' },
  { value: '-2', label: '2 boots ago (-b -2)' },
];

// ---------------------------------------------------------------------------
// Common recipes — each populates the builder and doubles as SEO content.
// ---------------------------------------------------------------------------

interface Recipe {
  title: string;
  description: string;
  state: Partial<BuilderState>;
}

const RECIPES: Recipe[] = [
  {
    title: 'Failed SSH logins',
    description: 'Hunt brute-force attempts in the sshd journal.',
    state: { unit: 'sshd', grep: 'Failed password', since: 'today' },
  },
  {
    title: 'Service crashes since last boot',
    description: 'Error-level messages from a unit on the current boot.',
    state: { unit: 'nginx', priority: 'err', boot: '0' },
  },
  {
    title: 'Kernel errors today',
    description: 'Kernel ring-buffer messages at error priority since midnight.',
    state: { kernel: true, priority: 'err', since: 'today' },
  },
  {
    title: 'Disk / I/O errors',
    description: 'Grep the journal for disk and I/O failure patterns.',
    state: { grep: 'I/O error|EXT4-fs error|ata[0-9]+', priority: 'err' },
  },
  {
    title: 'OOM killer events',
    description: 'Find out-of-memory kills recorded by the kernel.',
    state: { kernel: true, grep: 'Out of memory|oom-kill|Killed process' },
  },
  {
    title: 'Follow a service live',
    description: 'Tail a unit in real time, like tail -f.',
    state: { unit: 'docker', follow: true, lines: '50' },
  },
  {
    title: 'Errors since yesterday',
    description: 'All err+ messages across the system since yesterday.',
    state: { priority: 'err', since: 'yesterday' },
  },
  {
    title: 'Last 100 lines, newest first',
    description: 'Quick reverse view of the most recent journal entries.',
    state: { lines: '100', reverse: true },
  },
  {
    title: 'sudo / authentication activity',
    description: 'Track privilege escalation and auth events.',
    state: { grep: 'sudo|authentication failure|session opened', since: '24 hours ago' },
  },
  {
    title: 'Journal disk usage',
    description: 'Show how much disk the journal is consuming.',
    state: { diskUsage: true },
  },
  {
    title: 'Service logs as JSON',
    description: 'Machine-readable output for piping into jq or a log pipeline.',
    state: { unit: 'postgresql', output: 'json', since: '1 hour ago' },
  },
];

// ---------------------------------------------------------------------------
// Cheat-sheet reference rows
// ---------------------------------------------------------------------------

const CHEAT_SHEET: { flag: string; meaning: string }[] = [
  { flag: '-u, --unit=NAME', meaning: 'Show logs for a specific systemd unit (e.g. -u sshd).' },
  { flag: '-p, --priority=P', meaning: 'Filter by priority; a single value means "that level and more severe".' },
  { flag: '--since=, --until=', meaning: 'Time range. Accepts "today", "yesterday", "1 hour ago", or "YYYY-MM-DD HH:MM:SS".' },
  { flag: '-f, --follow', meaning: 'Follow the journal, printing new entries as they arrive (like tail -f).' },
  { flag: '-k, --dmesg', meaning: 'Show only kernel messages (the dmesg ring buffer).' },
  { flag: '-b, --boot[=ID]', meaning: 'Limit to a boot. -b is current, -b -1 the previous boot, etc.' },
  { flag: '-g, --grep=PATTERN', meaning: 'Filter the MESSAGE field by a Perl-compatible regular expression.' },
  { flag: '-o, --output=MODE', meaning: 'Output format: short, short-iso, cat, json, json-pretty, verbose.' },
  { flag: '-r, --reverse', meaning: 'Show newest entries first.' },
  { flag: '-n, --lines=N', meaning: 'Show the most recent N lines (implies tail behavior).' },
  { flag: '--disk-usage', meaning: 'Print how much disk space the journal currently occupies.' },
  { flag: '_UID=, _PID=, _COMM=', meaning: 'Match on journal fields, e.g. _UID=1000 for one user.' },
  { flag: '--no-pager', meaning: 'Do not pipe output through a pager (useful in scripts).' },
];

// ---------------------------------------------------------------------------
// Command + config generation (pure functions)
// ---------------------------------------------------------------------------

/** Quote a shell argument only when it contains characters the shell would interpret. */
function shellQuote(value: string): string {
  if (value === '') return "''";
  if (/^[A-Za-z0-9_./:=-]+$/.test(value)) return value;
  // Single-quote and escape any embedded single quotes.
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

function buildCommand(s: BuilderState): string {
  const parts: string[] = ['journalctl'];

  if (s.unit.trim()) parts.push('-u', shellQuote(s.unit.trim()));
  if (s.kernel) parts.push('-k');
  if (s.boot) parts.push('-b', s.boot === '0' ? '' : s.boot);
  if (s.priority) parts.push('-p', s.priority);
  if (s.since.trim()) parts.push('--since', shellQuote(s.since.trim()));
  if (s.until.trim()) parts.push('--until', shellQuote(s.until.trim()));
  if (s.grep.trim()) parts.push('-g', shellQuote(s.grep.trim()));
  if (s.uid.trim()) parts.push(`_UID=${s.uid.trim()}`);
  if (s.output) parts.push('-o', s.output);
  if (s.reverse) parts.push('-r');
  if (s.lines.trim()) parts.push('-n', s.lines.trim());
  if (s.follow) parts.push('-f');
  if (s.diskUsage) parts.push('--disk-usage');

  // Filter out the empty string produced by "-b" current-boot (journalctl -b with no arg).
  return parts.filter((p) => p !== '').join(' ');
}

/** Derive a safe Alert24 log-search name from the active filters. */
function deriveSearchName(s: BuilderState): string {
  const base =
    (s.unit.trim() || (s.kernel ? 'kernel' : '') || 'journal') +
    (s.grep.trim() ? '_matches' : s.priority ? `_${s.priority}` : '_events');
  return base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'journal_events';
}

interface JournaldSearch {
  name: string;
  source: 'journald';
  unit: string | null;
  pattern: string | null;
  min_priority: number | null;
}

/**
 * Build the Alert24 server-agent journald log_search block.
 * Schema per /docs/agent-log-searches.md:
 *   { name, source: "journald", unit, pattern, min_priority }
 * The agent runs `journalctl` for the unit/priority each interval and reports
 * the match count, which feeds the heartbeat alert-rule engine.
 */
function buildMonitorConfig(s: BuilderState): { search: JournaldSearch; note: string } {
  const search: JournaldSearch = {
    name: deriveSearchName(s),
    source: 'journald',
    unit: s.unit.trim() || null,
    pattern: s.grep.trim() || null,
    min_priority: s.priority ? PRIORITY_NUM[s.priority] : null,
  };

  let note = '';
  if (s.kernel) {
    note = 'Note: -k (kernel-only) has no journald equivalent in the agent schema — set a "unit" or "pattern" to scope the search instead.';
  } else if (!search.unit && !search.pattern && search.min_priority === null) {
    note = 'This search matches every journal line (pure volume tracking). Add a unit, pattern, or priority to make it specific.';
  }

  return { search, note };
}

function configToJson(search: JournaldSearch): string {
  return JSON.stringify({ log_searches: [search] }, null, 2);
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }, [text]);
  return (
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-[#2813e8] text-white hover:bg-[#1f0fb0] transition-colors"
      type="button"
    >
      {copied ? <Check size={15} /> : <Copy size={15} />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#2813e8] focus:ring-[#2813e8]"
      />
      <span>
        <span className="text-sm font-medium text-gray-800">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  );
}

const fieldClass =
  'w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2813e8] bg-white';
const labelClass = 'block text-sm font-semibold text-gray-800 mb-1.5';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function JournalctlBuilderTool() {
  const [state, setState] = useState<BuilderState>(DEFAULT_STATE);
  const [cheatOpen, setCheatOpen] = useState(false);

  const update = useCallback(<K extends keyof BuilderState>(key: K, value: BuilderState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const command = useMemo(() => buildCommand(state), [state]);
  const { search, note } = useMemo(() => buildMonitorConfig(state), [state]);
  const configJson = useMemo(() => configToJson(search), [search]);

  const applyRecipe = useCallback((recipe: Recipe) => {
    setState({ ...DEFAULT_STATE, ...recipe.state });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Generated command */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center gap-2 font-semibold text-gray-800">
            <Terminal size={18} /> Generated command
          </label>
          <CopyButton text={command} />
        </div>
        <pre className="w-full p-4 bg-gray-900 text-green-400 rounded-lg font-mono text-sm overflow-x-auto whitespace-pre-wrap break-all">
          {command}
        </pre>
        {state.follow && (
          <p className="mt-2 text-xs text-amber-600">
            -f (follow) keeps the command running. Combine with -n to show a backlog before tailing.
          </p>
        )}
      </div>

      {/* Builder form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        {/* Unit */}
        <div>
          <label className={labelClass} htmlFor="jc-unit">
            Unit / service (-u)
          </label>
          <input
            id="jc-unit"
            list="jc-unit-list"
            type="text"
            value={state.unit}
            onChange={(e) => update('unit', e.target.value)}
            placeholder="e.g. sshd, nginx, docker"
            className={fieldClass}
          />
          <datalist id="jc-unit-list">
            {UNIT_SUGGESTIONS.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </div>

        {/* Priority */}
        <div>
          <label className={labelClass} htmlFor="jc-priority">
            Priority (-p)
          </label>
          <select
            id="jc-priority"
            value={state.priority}
            onChange={(e) => update('priority', e.target.value as Priority)}
            className={fieldClass}
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Since */}
        <div>
          <label className={labelClass} htmlFor="jc-since">
            Since (--since)
          </label>
          <input
            id="jc-since"
            list="jc-since-list"
            type="text"
            value={state.since}
            onChange={(e) => update('since', e.target.value)}
            placeholder='"today", "1 hour ago", or "2026-06-01 09:00:00"'
            className={fieldClass}
          />
          <datalist id="jc-since-list">
            {SINCE_PRESETS.filter(Boolean).map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>

        {/* Until */}
        <div>
          <label className={labelClass} htmlFor="jc-until">
            Until (--until)
          </label>
          <input
            id="jc-until"
            list="jc-until-list"
            type="text"
            value={state.until}
            onChange={(e) => update('until', e.target.value)}
            placeholder='"now", "yesterday", or a timestamp'
            className={fieldClass}
          />
          <datalist id="jc-until-list">
            {UNTIL_PRESETS.filter(Boolean).map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>

        {/* Grep */}
        <div>
          <label className={labelClass} htmlFor="jc-grep">
            Match pattern (-g)
          </label>
          <input
            id="jc-grep"
            type="text"
            value={state.grep}
            onChange={(e) => update('grep', e.target.value)}
            placeholder="e.g. Failed password (regex)"
            className={fieldClass}
          />
        </div>

        {/* UID */}
        <div>
          <label className={labelClass} htmlFor="jc-uid">
            Specific user (_UID=)
          </label>
          <input
            id="jc-uid"
            type="text"
            inputMode="numeric"
            value={state.uid}
            onChange={(e) => update('uid', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 1000"
            className={fieldClass}
          />
        </div>

        {/* Boot */}
        <div>
          <label className={labelClass} htmlFor="jc-boot">
            Boot (-b)
          </label>
          <select
            id="jc-boot"
            value={state.boot}
            onChange={(e) => update('boot', e.target.value)}
            className={fieldClass}
          >
            {BOOT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Output */}
        <div>
          <label className={labelClass} htmlFor="jc-output">
            Output format (-o)
          </label>
          <select
            id="jc-output"
            value={state.output}
            onChange={(e) => update('output', e.target.value as OutputFormat)}
            className={fieldClass}
          >
            {OUTPUT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Line limit */}
        <div>
          <label className={labelClass} htmlFor="jc-lines">
            Line limit (-n)
          </label>
          <input
            id="jc-lines"
            type="text"
            inputMode="numeric"
            value={state.lines}
            onChange={(e) => update('lines', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 100"
            className={fieldClass}
          />
        </div>

        {/* Flags */}
        <div className="flex flex-col gap-3 justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
          <Toggle label="Follow mode (-f)" checked={state.follow} onChange={(v) => update('follow', v)} hint="Tail new entries live" />
          <Toggle label="Kernel only (-k)" checked={state.kernel} onChange={(v) => update('kernel', v)} hint="dmesg ring buffer" />
          <Toggle label="Reverse (-r)" checked={state.reverse} onChange={(v) => update('reverse', v)} hint="Newest first" />
          <Toggle label="Disk usage (--disk-usage)" checked={state.diskUsage} onChange={(v) => update('diskUsage', v)} hint="Journal size on disk" />
        </div>
      </div>

      {/* Reset */}
      <div className="mb-10">
        <button
          type="button"
          onClick={() => setState(DEFAULT_STATE)}
          className="text-sm font-medium text-gray-600 hover:text-[#2813e8] underline"
        >
          Reset all fields
        </button>
      </div>

      {/* Common recipes */}
      <section className="mb-10" aria-labelledby="recipes-heading">
        <h2 id="recipes-heading" className="text-xl font-bold text-gray-900 mb-1">
          Common journalctl recipes
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Click any example to load it into the builder above.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {RECIPES.map((r) => (
            <button
              key={r.title}
              type="button"
              onClick={() => applyRecipe(r)}
              className="text-left p-4 rounded-lg border border-gray-200 bg-white hover:border-[#2813e8] hover:shadow-sm transition-colors"
            >
              <div className="font-semibold text-sm text-gray-900">{r.title}</div>
              <div className="text-xs text-gray-600 mt-1">{r.description}</div>
              <code className="block text-xs text-[#2813e8] font-mono mt-2 break-all">
                {buildCommand({ ...DEFAULT_STATE, ...r.state })}
              </code>
            </button>
          ))}
        </div>
      </section>

      {/* Alert24 monitoring config companion */}
      <section className="mb-10" aria-labelledby="monitor-heading">
        <div className="rounded-xl border border-[#2813e8]/30 bg-[#2813e8]/5 p-5">
          <div className="flex items-center justify-between mb-2">
            <h2 id="monitor-heading" className="flex items-center gap-2 text-lg font-bold text-gray-900">
              <Bell size={18} className="text-[#2813e8]" /> Run this check continuously and get alerted
            </h2>
            <CopyButton text={configJson} label="Copy config" />
          </div>
          <p className="text-sm text-gray-700 mb-3">
            A one-off <code className="font-mono">journalctl</code> command only tells you what happened when you run it.
            Drop this <code className="font-mono">log_searches</code> block into your{' '}
            <strong>Alert24 server agent</strong> config and the agent re-runs the same journald query every interval,
            reporting the match count so you get paged the moment it spikes (e.g. &gt;10 SSH failures in a minute).
          </p>
          <pre className="w-full p-4 bg-gray-900 text-blue-200 rounded-lg font-mono text-xs overflow-x-auto whitespace-pre">
            {configJson}
          </pre>
          {note && <p className="mt-2 text-xs text-amber-700">{note}</p>}
          <p className="mt-3 text-xs text-gray-600">
            Log-search monitoring is an Alert24 Pro/Enterprise feature. Every plan includes server agents
            (3 free; 5 per unit on paid plans), and the journald source runs on Linux hosts only.
          </p>
        </div>
      </section>

      {/* CTA → Alert24 log monitoring */}
      <Alert24CTA variant="log-search" source="journalctl-builder" />

      {/* Cheat sheet (collapsible) */}
      <section className="mt-10" aria-labelledby="cheat-heading">
        <button
          type="button"
          onClick={() => setCheatOpen((o) => !o)}
          className="flex items-center gap-2 w-full text-left text-lg font-bold text-gray-900"
          aria-expanded={cheatOpen}
        >
          {cheatOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <span id="cheat-heading">journalctl flag cheat sheet</span>
        </button>
        {cheatOpen && (
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 border text-left font-semibold">Flag</th>
                  <th className="px-4 py-2 border text-left font-semibold">What it does</th>
                </tr>
              </thead>
              <tbody>
                {CHEAT_SHEET.map((row) => (
                  <tr key={row.flag}>
                    <td className="px-4 py-2 border font-mono whitespace-nowrap align-top">{row.flag}</td>
                    <td className="px-4 py-2 border text-gray-700">{row.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
